import { CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, ModuleWithProviders } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthGuardService } from '@app/core/guards/auth-guard.service';
import { ErrorInterceptor } from '@app/core/interceptor/error-interceptor';
import { DropdownComponent } from '@app/core/ui/dropdown/dropdown.component';
import { ModalComponent } from '@app/core/ui/modal/modal.component';
import { BreadcrumbsComponent } from '@app/core/ui/breadcrumbs/breadcrumbs.component';
import { PageLimitComponent } from './ui/page-limit/page-limit.component';
import { PriceComponent } from './ui/price/price.component';
import { PaginationComponent } from './ui/pagination/pagination.component';
import { ConfirmModalComponent } from './ui/confirm-modal/confirm-modal.component';
import { AddToCartComponent } from './ui/add-to-cart/add-to-cart.component';
import { StockIndicatorComponent } from './ui/stock-indicator/stock-indicator.component';
import { TranslateModule } from '@ngx-translate/core';
import { ContextService } from '@app/core/service/context.service';
import { ModalService } from '@app/core/ui/modal/modal.service';
import { DomService } from '@app/core/ui/modal/dom.service';
import { TranslationService } from '@app/core/service/translation.service';
import { LoginGuardService } from '@app/core/guards/login-guard.service';
import { RouterModule } from '@angular/router';
import { FilterService } from '@app/core/service/filter.service';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgChartsModule } from 'ng2-charts';
import { ModalFactoryService } from '@app/core/ui/modal/modal-factory.service';
import { ContextGuardService } from '@app/core/guards/context-guard.service';
import { LoaderService } from '@app/core/business/ui/loader.service';
import { LoaderInterceptor } from '@app/core/interceptor/loader-interceptor';
import { AuthenticationService } from '@app/core/business/user/authentication.service';
import { StringToDatePipe } from './pipes/string-to-date.pipe';
import { HistoryMobileListComponent } from '@app/core/ui/history-mobile-list/history-mobile-list.component';
import { HistoryMobileListItemComponent } from '@app/core/ui/history-mobile-list-item/history-mobile-list-item.component';
import { DebounceClickDirective } from './directive/debounce-click.directive';
import { AvailabilityComponent } from './ui/availability/availability.component';
import { CatalogComponent } from '@app/core/ui/catalog/catalog.component';
import { ProductCardComponent } from '@app/core/ui/product-card/product-card.component';
import { ScrollOnPageChangeDirective } from '@app/core/directive/scroll-on-page-change.directive';
import { DatePipe } from '@angular/common';
import { AdvertisementComponent } from './ui/advertisement/advertisement.component';
import { CultureInterceptor } from './interceptor/culture-interceptor';
import { TagLabelComponent } from './ui/tag-label/tag-label.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { AsNumberArrayPipe } from './pipes/as-number-array.pipe';
import { OrderSelectorModalComponent } from './ui/order-selector-modal/order-selector-modal.component';
import { JumpToPageComponent } from './ui/jump-to-page/jump-to-page.component';
import { ScanPipe } from 'ngx-pipes';
import { CacheInterceptor } from './interceptor/cache-interceptor';
import { PageLimitLabelComponent } from './ui/page-limit-label/page-limit-label.component';
import { AdditionalGuarantiesComponent } from './ui/additional-guaranties/additional-guaranties.component';
import { ProductPictureComponent } from '@app/core/ui/product-picture/product-picture.component';
import { BillPaymentComponent } from './ui/bill-payment/bill-payment.component';
import { OnlinePaymentTransactionComponent } from './ui/online-payment-transaction/online-payment-transaction.component';
import { OnlinePaymentTransactionCAComponent } from './ui/online-payment-transaction-ca/online-payment-transaction-ca.component';
import { AdvertisementWhileWaitingComponent } from './ui/advertisement-while-waiting/advertisement-while-waiting.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PictoComponent } from './ui/picto/picto.component';
import { CapitalizeFirstLetterPipe } from './pipes/capitalize-first-letter.pipe';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { FrancoComponent } from './ui/franco/franco.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { TooltipComponent } from './ui/tooltip/tooltip.component';
import { TooltipModule } from './ui/tooltip/tooltip.module';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { EuroSymbolPipe } from './pipes/EuroSymbole.pipe';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SlicerPipe } from './pipes/slicer.pipe';
import { ApiService } from './service/api-ws.service';
import { ApiNameInterceptor } from './interceptor/apiname-interceptor';

import { RefreshInterceptor } from './interceptor/refresh-interceptor';
import { AuthInterceptor } from './interceptor/auth-interceptor';
import { ServerErrorInterceptor } from './interceptor/server-error-interceptor';

import { CodeProduitSuffixPipe } from './pipes/codeProduitSuffixDCPipe.pipe';
import { PlaytestService } from './service/playtest.service';
import { ErrorCardComponent } from '@app/ui/error-card/error-card.component';
import { ZXingScannerModule } from '@zxing/ngx-scanner';



@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    RouterModule,
    TranslateModule,
    MatDatepickerModule,
    FormsModule,
    MatMomentDateModule,
    MatFormFieldModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatTableModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    InfiniteScrollModule,
    MatTooltipModule,
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    TooltipModule,
    NgxExtendedPdfViewerModule,
    ZXingScannerModule
  ],
  exports: [
    BreadcrumbsComponent,
    MatAutocompleteModule,
    DropdownComponent,
    ModalComponent,
    PageLimitComponent,
    PriceComponent,
    PaginationComponent,
    StockIndicatorComponent,
    AddToCartComponent,
    HistoryMobileListComponent,
    HistoryMobileListItemComponent,
    ProductCardComponent,
    CatalogComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule, MatFormFieldModule, MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    NgxDatatableModule,
    NgChartsModule,
    StringToDatePipe,
    SlicerPipe,
    SafeHtmlPipe,
    AsNumberArrayPipe,
    CodeProduitSuffixPipe,
    DebounceClickDirective,
    ScrollOnPageChangeDirective,
    AvailabilityComponent,
    AdvertisementComponent,
    TagLabelComponent,
    JumpToPageComponent,
    PageLimitLabelComponent,
    AdditionalGuarantiesComponent,
    ProductPictureComponent,
    BillPaymentComponent,
    OnlinePaymentTransactionComponent,
    OnlinePaymentTransactionCAComponent,
    AdvertisementWhileWaitingComponent,
    MatDatepickerModule,
    MatMomentDateModule,
    PictoComponent,
    CapitalizeFirstLetterPipe,
    MatTableModule,
    FrancoComponent,
    MatTooltipModule,
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    TooltipModule,
    NgxExtendedPdfViewerModule,
    EuroSymbolPipe,
    ErrorCardComponent,
    ZXingScannerModule
  ],
  declarations: [
    DropdownComponent,
    ModalComponent,
    BreadcrumbsComponent,
    PageLimitComponent,
    PriceComponent,
    PaginationComponent,
    ConfirmModalComponent,
    StockIndicatorComponent,
    AddToCartComponent,
    HistoryMobileListComponent,
    HistoryMobileListItemComponent,
    ProductCardComponent,
    CatalogComponent,
    StringToDatePipe,
    SlicerPipe,
    SafeHtmlPipe,
    DebounceClickDirective,
    ScrollOnPageChangeDirective,
    AvailabilityComponent,
    AdvertisementComponent,
    TagLabelComponent,
    AsNumberArrayPipe,
    CodeProduitSuffixPipe,
    OrderSelectorModalComponent,
    JumpToPageComponent,
    PageLimitLabelComponent,
    AdditionalGuarantiesComponent,
    ProductPictureComponent,
    BillPaymentComponent,
    OnlinePaymentTransactionComponent,
    OnlinePaymentTransactionCAComponent,
    AdvertisementWhileWaitingComponent,
    PictoComponent,
    CapitalizeFirstLetterPipe,
    FrancoComponent,
    EuroSymbolPipe,
    ErrorCardComponent
  ],
  providers: [
    DatePipe,
    ScanPipe,
    StringToDatePipe,
    SlicerPipe
    /*
     * Add providers inside CoreModule.ForRoot() to prevent them having
     * one instance per module importing CoreModule instead of only one for all
    */
  ]
})
export class CoreModule {
  /**
   * Allow us to use same providers instances for all modules importing coreModule
   * More informations : https://stackoverflow.com/questions/39664861/importing-modules-with-forroot/39665993#39665993
   */
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        AuthenticationService,
        AuthGuardService,
        PlaytestService,
        ContextGuardService,
        ContextService,

        /**
         * JDU: On a beaucoup d'interceptors, j'ai séparer selon leurs rôles, l'ordre est extrêmement important.
         * ATTENTION, les intercepteurs sont appelés dans l'ordre où ils sont définis ici, mais les réponses
         * passent dans l'ordre inverse.
         */

        // 🔑 Ajout du token dans chaque requête
        {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthInterceptor,
          multi: true,
        },

      

        // 🌍 Ajout culture/locale dans les headers
        {
          provide: HTTP_INTERCEPTORS,
          useClass: CultureInterceptor,
          multi: true,
        },

        // 🗃️ Mise en cache des requêtes GET
        {
          provide: HTTP_INTERCEPTORS,
          useClass: CacheInterceptor,
          multi: true,
        },

        // 🔀 Ajout du X-Api-Name (multi-API Gateway)
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ApiNameInterceptor,
          multi: true,
        },

        // 🧾 Gestion des erreurs « métier » (arraybuffer → JSON, messages, commandes invalides)
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ServerErrorInterceptor, // <-- renommer ErrorInterceptorArrayBuffer
          multi: true,
        },

        // 🚪 Dernière ligne de défense : Gestion globale des erreurs, logout forcé si encore un 401 après refresh
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorInterceptor,
          multi: true,
        },

        // ♻️ Gestion du refresh token si 401
        {
          provide: HTTP_INTERCEPTORS,
          useClass: RefreshInterceptor,
          multi: true,
        },

          // ⏳ Gestion affichage loader global
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LoaderInterceptor,
          multi: true,
        },
        {
          provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
          useValue: { strict: true }
        },
        {
          provide: MAT_DATE_FORMATS,
          useValue: MAT_MOMENT_DATE_FORMATS
        },

        DomService,
        ModalService,
        LoaderService,
        TranslationService,
        LoginGuardService,
        FilterService,
        ModalFactoryService,
        ApiService
      ],
    };
  }
}
